import {
  CeremonySchema,
  EventNoticeResponseSchema,
  StationBadgeSchema,
  StationChannelArtSchema,
  StationDashboardResponseSchema,
  StationDashboardRankSchema,
  StationDashboardSubscriptionSchema,
  StationDetailSchema,
  StationDisplaySchema,
  StationMedalSchema,
  StationSchema,
  StationStatsSchema,
  StationSubscriptionSchema,
  StationUserAuthSchema,
  StationDashboardLinkSchema,
  StationDashboardStationSchema,
  StationDashboardUpdSchema,
  StationAuthenticationResponseSchema,
  StationLinkSchema,
} from './station.schema';
import {
  CeremonyModel,
  EventNoticeResponseModel,
  StationAuthenticationModel,
  StationBadgeModel,
  StationChannelArtModel,
  StationDashboardLinkModel,
  StationDashboardModel,
  StationDashboardRankModel,
  StationDashboardStationModel,
  StationDashboardSubscriptionModel,
  StationDashboardUpdModel,
  StationDetailModel,
  StationDisplayModel,
  StationLinkModel,
  StationMedalModel,
  StationModel,
  StationStatsModel,
  StationSubscriptionModel,
  StationUserPermissionsModel,
} from './station.model';

// Note. 최초 구현 시엔 Model을 서버 응답 객체와 동일하게 맞췄지만, 추후 응답에 변화가 생긴다면 여기에서 파싱 로직 구현 필요
export const mapEventNoticeSchemaToModel = (
  schema: EventNoticeResponseSchema,
): EventNoticeResponseModel => ({
  fixedEventData: schema.fix,
  nonFixedEventData: schema.nonFix,
});

export const mapCeremonySchemaToModel = (
  schema: CeremonySchema,
): CeremonyModel => ({
  ceremonyAdBalloonImg: schema.ceremonyAdballonImg,
  ceremonyStarballoon: schema.ceremonyStarballoon,
  data: schema.data,
  receiverId: schema.receiverId,
  senderId: schema.senderId,
});

export const mapStationSchemaToModel = (
  schema: StationSchema,
): StationModel => ({
  detail: schema.station ? mapStationDetailSchemaToModel(schema.station) : null,
  display: schema.display
    ? mapStationDisplaySchemaToModel(schema.display)
    : null,
  channelArt: schema.channelArt
    ? mapStationChannelArtSchemaToModel(schema.channelArt)
    : null,
  badges: schema.badges.map(mapStationBadgeSchemaToModel),
  medals: schema.medals.map(mapStationMedalSchemaToModel),
  stats: schema.upd ? mapStationStatsSchemaToModel(schema.upd) : null,
  subscription: schema.subscription
    ? mapStationSubscriptionSchemaToModel(schema.subscription)
    : null,
  userPermissions: schema.userAuth
    ? mapStationUserAuthSchemaToModel(schema.userAuth)
    : null,
  links: schema.link.map(mapStationLinkSchemaToModel),
});

export const mapStationLinkSchemaToModel = (
  schema: StationLinkSchema,
): StationLinkModel => ({
  userId: schema.userId,
  type: schema.type,
  url: schema.url,
  image: schema.image,
  linkName: schema.linkName,
});

const mapStationStatsSchemaToModel = (
  schema: StationStatsSchema,
): StationStatsModel => ({
  fanCount: schema.fanCnt,
});

export const mapStationSubscriptionSchemaToModel = (
  schema: StationSubscriptionSchema,
): StationSubscriptionModel => ({
  totalCount: schema.totalCnt,
});

export const mapStationUserAuthSchemaToModel = (
  schema: StationUserAuthSchema,
): StationUserPermissionsModel => ({
  isFavorite: schema.isFavorite,
  isMobilePushEnabled: schema.isMobilePush,
  isSubscribed: schema.isSubscribe,
  isOwner: schema.isOwner,
  isManager: schema.isManager,
  canPostNotice: schema.isNotice,
  hasAdRevenue: schema.isAdsence,
});

export const mapStationBadgeSchemaToModel = (
  schema: StationBadgeSchema,
): StationBadgeModel => ({
  id: schema.idx,
  groupId: schema.groupNo,
  priority: schema.priority,
  groupName: schema.groupName,
  groupClassName: schema.groupClassName,
  groupBackgroundColor: schema.groupBackgroundColor,
});

export const mapStationMedalSchemaToModel = (
  schema: StationMedalSchema,
): StationMedalModel => ({
  userId: schema.userId,
  name: schema.medalName,
  content: schema.medalContent,
  imageKey: schema.medalImageKey,
  description: schema.medalDescription,
});

export const mapStationDisplaySchemaToModel = (
  schema: StationDisplaySchema,
): StationDisplayModel => ({
  id: schema.stationNo,
  userId: schema.userId,
  mainType: schema.mainType,
  titleType: schema.titleType,
  titleText: schema.titleText,
  profileText: schema.profileText,
  skinType: schema.skinType,
  titleSkinImageUrl: schema.titleSkinImage,
  aiProfileConfYn: schema.aiProfileConfYn,
  aiProfileStr: schema.aiProfileStr,
});

export const mapStationChannelArtSchemaToModel = (
  schema: StationChannelArtSchema,
): StationChannelArtModel => ({
  pcFilename: schema.pcFilename,
  mobileFilename: schema.mobileFilename,
  themeColor: schema.color,
  createdAt: schema.regDate,
});

export const mapStationDetailSchemaToModel = (
  schema: StationDetailSchema,
): StationDetailModel => ({
  id: schema.stationNo,
  userId: schema.userId,
  nickname: schema.userNick,
  name: schema.stationName,
  title: schema.stationTitle,
  totalBroadcastTime: schema.totalBroadTime,
  grade: schema.grade,
  joinedAt: schema.joinTime,
  country: schema.country,
  currentTimestamp: schema.currentTimestamp,
  activeNo: schema.activeNo,
  profileImageUrl: schema.profileImage,
  isSubscriptionVisible: schema.subscribeVisible === 'on',
  liveStartedAt: schema.broadStart,
});

export const mapStationDashboardSchemaToModel = (
  schema: StationDashboardResponseSchema,
): StationDashboardModel => ({
  subscription: mapStationDashboardSubscriptionSchemaToModel(
    schema.subscription,
  ),
  rank: schema.rank.map(mapStationDashboardRankSchemaToModel),
  link: schema.link.map(mapStationDashboardLinkSchemaToModel),
  station: mapStationDashboardStationSchemaToModel(schema.station),
  upd: mapStationDashboardUpdSchemaToModel(schema.upd),
  fanclubCount: schema.fanclubCnt,
  supporterCount: schema.supporterCnt,
});

export const mapStationDashboardSubscriptionSchemaToModel = (
  schema: StationDashboardSubscriptionSchema,
): StationDashboardSubscriptionModel => ({
  total: Number(schema.total),
  tier1: Number(schema.tier1),
  tier2: Number(schema.tier2),
});

export const mapStationDashboardRankSchemaToModel = (
  schema: StationDashboardRankSchema,
): StationDashboardRankModel => ({
  rankType: schema.rankType,
  rankName: schema.rankName,
  totalRank: schema.totalRank,
  totalRankLast: schema.totalRankLast,
});

export const mapStationDashboardLinkSchemaToModel = (
  schema: StationDashboardLinkSchema,
): StationDashboardLinkModel => ({
  type: schema.type,
  url: schema.url,
  image: schema.image,
  code: schema.code,
  linkName: schema.linkName,
});

export const mapStationDashboardStationSchemaToModel = (
  schema: StationDashboardStationSchema,
): StationDashboardStationModel => ({
  id: schema.stationNo,
  nickname: schema.userNick,
  totalBroadcastTime: schema.totalBroadTime,
  joinedAt: schema.joinTime,
});

export const mapStationDashboardUpdSchemaToModel = (
  schema: StationDashboardUpdSchema,
): StationDashboardUpdModel => ({
  userId: schema.userId,
  aspCode: schema.aspCode,
  fanCount: schema.fanCnt,
  today0VisitCount: schema.today0VisitCnt,
  today1VisitCount: schema.today1VisitCnt,
  totalVisitCount: Number(schema.totalVisitCnt),
  today0OkCount: schema.today0OkCnt,
  today1OkCount: schema.today1OkCnt,
  today0FavoriteCount: schema.today0FavCnt,
  today1FavoriteCount: schema.today1FavCnt,
  totalOkCount: Number(schema.totalOkCnt),
  totalViewCount: Number(schema.totalViewCnt),
});

export const mapStationAuthenticationSchemaToModel = (
  schema: StationAuthenticationResponseSchema,
): StationAuthenticationModel => ({
  profileImage: schema.profileImage,
  userId: schema.userId,
  nickname: schema.nickname,
  isRealNameCheck: schema.isRealnameCheck,
  noteCount: schema.noteCnt,
  isBestBj: schema.isBestBj,
  isPartnerBj: schema.isPartnerBj,
  isAbroadJoin: schema.isAbroadJoin,
  isAfSupportersBj: schema.isAfSupportersBj,
});
